import { useEffect } from 'react'
import { Link, NavLink, Navigate, Outlet, useNavigate } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { PageContainer } from '@/components/shared/page-container'
import { ThemeToggle } from '@/components/ThemeToggle'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { getDefaultRouteForRole } from '@/lib/auth'
import { getCurrentUserRole, signOutUser } from '@/lib/api'
import { getErrorMessage } from '@/lib/error-messages'
import { queryKeys } from '@/lib/query-keys'
import { authEvents } from '@/lib/auth-events'
import { cn } from '@/lib/utils'
import { Footer } from './footer'

const links = [
  { to: '/trainer/sessions', label: 'Termine' },
  { to: '/trainer/meine-kurse', label: 'Meine Kurse' },
  { to: '/trainer/customers', label: 'Teilnehmer' },
  { to: '/trainer/umfragen', label: 'Umfragen' },
  { to: '/trainer/handbuch', label: 'Handbuch' },
]

export function TrainerLayout() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const roleQuery = useQuery({
    queryKey: queryKeys.currentUserProfile,
    queryFn: getCurrentUserRole,
    retry: false,
    refetchOnWindowFocus: false,
  })

  useEffect(
    () =>
      authEvents.subscribe(() => {
        queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
      }),
    [queryClient],
  )

  const logoutMutation = useMutation({
    mutationFn: signOutUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.currentUserProfile })
      toast.success('Erfolgreich ausgeloggt.')
      navigate('/login', { replace: true })
    },
    onError: (error) => {
      toast.error(getErrorMessage(error))
    },
  })

  if (roleQuery.isLoading) {
    return (
      <PageContainer className="py-12">
        <Skeleton className="h-8 w-64" />
      </PageContainer>
    )
  }

  if (!roleQuery.data || !roleQuery.data.is_active) {
    return <Navigate to="/login" replace />
  }

  if (roleQuery.data.role !== 'trainer') {
    return <Navigate to={getDefaultRouteForRole(roleQuery.data.role)} replace />
  }

  return (
    <div className="h-dvh overflow-hidden font-body text-foreground flex flex-col">
      <header className="glass sticky top-0 z-50 border-b border-border/50 shadow-lg">
        <PageContainer className="flex h-16 items-center justify-between">
          <div className="flex items-center gap-8">
            <Link
              to="/trainer/meine-kurse"
              className="font-headline flex items-center gap-2 tracking-wider transition-opacity hover:opacity-90"
            >
              <img src="/motionlab.png" alt="motionLAB Logo" className="h-10 w-auto object-contain" />
              <div className="flex items-center text-lg font-bold">
                <span className="text-foreground">motion</span>
                <span className="text-gradient ml-[-1px]">LAB</span>
                <span className="ml-2 text-xs font-medium text-muted-foreground uppercase tracking-widest">
                  Trainer
                </span>
              </div>
            </Link>

            <nav className="hidden items-center gap-3 sm:flex">
              {links.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  className={({ isActive }) =>
                    cn(
                      'rounded-md px-3 py-1.5 font-nav text-sm font-medium transition-all hover:text-primary',
                      isActive ? 'bg-primary/20 text-primary' : 'text-foreground/80 hover:bg-muted',
                    )
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-2">
            <ThemeToggle />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="outline" size="sm" className="sm:hidden">
                  Menü
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-52">
                {links.map((link) => (
                  <DropdownMenuItem key={link.to} asChild>
                    <Link to={link.to}>{link.label}</Link>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onSelect={() => logoutMutation.mutate()}
                  disabled={logoutMutation.isPending}
                >
                  {logoutMutation.isPending ? 'Logout ...' : 'Logout'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="hidden font-nav text-sm font-medium text-foreground/80 transition-all hover:bg-muted hover:text-primary md:inline-flex"
              onClick={() => logoutMutation.mutate()}
              disabled={logoutMutation.isPending}
            >
              Logout
            </Button>
          </div>
        </PageContainer>
      </header>

      <main className="flex-1 min-h-0">
        <PageContainer className="h-full overflow-y-auto py-8">
          <Outlet />
        </PageContainer>
      </main>
      <div className="shrink-0"><Footer showGitHash /></div>
    </div>
  )
}
